import { css } from "@emotion/react";
import styled from "@emotion/styled";
import clsx from "clsx";
import { omit } from "lodash/fp";
import { useState } from "react";

import Clickable from "./Clickable.jsx";
import generateUniqueId from "./generateUniqueId.js";
import handleComponentsProp from "./handleComponentsProp.js";
import Icon from "./Icon.jsx";
import isPropValid from "./isPropValid.js";
import parseStyle from "./parseStyle.js";
import useThemeProps from "./useThemeProps.jsx";

const DetailsRoot = styled("div", {
  shouldForwardProp: (prop) => isPropValid(prop) && !["style"].includes(prop),
})`
  ${({ theme, style }) => parseStyle(style, theme)}
`;

const DetailsSummary = styled("div", {
  shouldForwardProp: isPropValid,
})``;

const DetailsSummaryToggle = styled(Clickable, {
  shouldForwardProp: isPropValid,
})`
  ${({ theme }) => theme.styleUtils.buttonReset}
  display: flex;
  width: 100%;
  align-items: center;
  cursor: pointer;
  gap: ${({ theme }) => theme.getLength([2, 4])};
`;

const DetailsSummaryToggleIcon = styled(Icon, {
  shouldForwardProp: isPropValid,
})`
  margin-inline-start: auto;
  flex: none;
`;

const DetailsSummaryContent = styled("span", {
  shouldForwardProp: isPropValid,
})``;

const DetailsContent = styled("div", {
  shouldForwardProp: isPropValid,
})`
  margin-block-start: ${({ ownerState, theme }) =>
    theme.getLength(ownerState.spacing)};
`;

export default function Details(props) {
  props = useThemeProps({ props: omit(["align"], props), name: "Details" });

  let {
    className,
    summary,
    children,
    id,
    isInitiallyExpanded = false,
    isExpanded,
    spacing = 4,
    onToggle = () => {
      setIsStatefullyExpanded(!isStatefullyExpanded);
    },
    components,
    printable = false,
    ...restProps
  } = props;

  let defaultComponents = {
    Summary: DetailsSummary,
    SummaryToggle: DetailsSummaryToggle,
    SummaryToggleIcon: DetailsSummaryToggleIcon,
    SummaryContent: DetailsSummaryContent,
    Content: DetailsContent,
  };

  components = handleComponentsProp(components, defaultComponents);
  let { Summary, SummaryToggle, SummaryToggleIcon, SummaryContent, Content } =
    components;

  let [isStatefullyExpanded, setIsStatefullyExpanded] =
    useState(isInitiallyExpanded);

  isExpanded = isExpanded ?? isStatefullyExpanded;

  let [defaultId] = useState(`details-${generateUniqueId()}`);

  id = id ?? defaultId;

  let ownerState = {
    summary,
    id,
    isInitiallyExpanded,
    onToggle,
    defaultComponents,
    components,
    isExpanded,
    spacing,
    ...restProps,
  };

  return (
    <DetailsRoot
      className={clsx(className, "wsui-details", {
        "wsui-is-expanded": isExpanded,
      })}
      ownerState={ownerState}
      id={id}
      {...restProps}
    >
      <Summary ownerState={ownerState}>
        <SummaryToggle
          className={clsx("wsui-details-summary-toggle", {})}
          onClick={(event) => {
            onToggle(isExpanded, event);
          }}
          aria-controls={`${id}-content`}
          aria-expanded={isExpanded}
          ownerState={ownerState}
        >
          <SummaryContent id={`${id}-summary-content`} ownerState={ownerState}>
            {summary}
          </SummaryContent>
          <SummaryToggleIcon
            ownerState={ownerState}
            name={isExpanded ? "chevron-up" : "chevron-down"}
            aria-hidden={true}
          />
        </SummaryToggle>
      </Summary>
      <Content
        id={`${id}-content`}
        aria-labelledby={`${id}-summary-content`}
        hidden={!isExpanded && !printable}
        css={css`
          ${printable &&
          !isExpanded &&
          css`
            @media not print {
              display: none;
            }
          `}
        `}
        ownerState={ownerState}
      >
        {isExpanded || printable ? children : null}
      </Content>
    </DetailsRoot>
  );
}
